import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchProducts, createProduct, updateProduct, deleteProduct, bulkDeleteProducts,
  fetchProductCategories, fetchSuppliers, fetchSupplierProducts,
} from '../../store/slices/productSlice';
import { fetchSemiFinished, updateSemiFinished } from '../../store/slices/semiFinishedSlice';
import { fetchDishes, updateDish } from '../../store/slices/dishSlice';
import SearchSelect from '../../components/SearchSelect';
import B52Modal from '../../components/B52Modal';
import B52Autocomplete from '../../components/B52Autocomplete';
import { BaseFood, Dish, Product, SemiFinishedProduct, SupplierProduct } from '../../types';
import TablePreferencesPanel from '../../components/TablePreferencesPanel';
import { TablePreferenceColumn, useTablePreferences } from '../../hooks/useTablePreferences';
import { openAndPrintHtml } from '../../utils/printDocument';
import { getHistoricalWriteOffUnavailableNotice } from '../../utils/operatingContext';
import { useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import { useSupplierDataScope } from '../../hooks/useSupplierDataScope';

const WEIGHT_TYPES = [
  { value: 'kg', label: 'кг' },
  { value: 'g', label: 'г' },
  { value: 'l', label: 'л' },
  { value: 'ml', label: 'мл' },
  { value: 'pcs', label: 'шт' },
];

const PAGE_SIZE = 15;
type ProductSort = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'category-asc' | 'category-desc';

type ProductForm = {
  name: string;
  b52Code: string;
  b52Name: string;
  price: string;
  criticalStockThreshold: string;
  weightType: string;
  categoryId: string;
  supplierId: string;
  description: string;
  isActive: boolean;
  allergenIds: number[];
  baseFoodId: string;
};

type ProductColumnKey = 'id' | 'name' | 'price' | 'unit' | 'category' | 'supplier' | 'actions';
const PRODUCT_TABLE_COLUMNS: readonly TablePreferenceColumn<ProductColumnKey>[] = [
  { key: 'id', label: '#', defaultWidth: 4, minWidth: 3, maxWidth: 15 },
  { key: 'name', label: 'Назва', defaultWidth: 18, minWidth: 8, maxWidth: 45, locked: true },
  { key: 'price', label: 'Ціна', defaultWidth: 6, minWidth: 5, maxWidth: 20 },
  { key: 'unit', label: 'Од.', defaultWidth: 5, minWidth: 5, maxWidth: 20 },
  { key: 'category', label: 'Категорія', defaultWidth: 10, minWidth: 6, maxWidth: 30 },
  { key: 'supplier', label: 'Постачальник', defaultWidth: 10, minWidth: 6, maxWidth: 30 },
  { key: 'actions', label: 'Дії', defaultWidth: 10, minWidth: 6, maxWidth: 25, locked: true },
];
const PRODUCT_TABLE_PREFERENCES_KEY = 'crm4chef.products.table-preferences.v1';
const LEGACY_PRODUCT_COLUMN_WIDTHS_KEY = 'crm4chef.products.columnWidths.v1';

type UsageEntry = {
  kind: 'dish' | 'semi';
  id: number;
  name: string;
  quantity: number;
};
type ProductDeleteReference = {
  scope: 'inventory-receipt' | 'inventory-revision' | 'inventory-writeoff' | 'purchase-worksheet' | string;
  documentId: number;
  documentLabel: string;
  date: string | null;
  lineId: number;
};
type DeleteBlockModalState = {
  title: string;
  message: string;
  entries: UsageEntry[];
  references: ProductDeleteReference[];
  productId?: number;
};
type ProductDetailsModalState = {
  product: Product;
  baseFood: BaseFood | null;
  baseFoodLoading: boolean;
  baseFoodError: string | null;
  usageEntries: UsageEntry[];
  linkedSupplierProducts: SupplierProduct[];
  receiptEntries: ProductReceiptEntry[];
  usageReportEntries: ProductUsageReportEntry[];
  activeTab: 'usage' | 'receipts' | 'usage-report';
  receiptsLoading: boolean;
  receiptsError: string | null;
};

type ProductReceiptEntry = {
  receiptId: number;
  date: string;
  invoiceNumber: string;
  supplierName?: string | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  supplierVatRate: number;
  vatAmount: number;
  weightType: string;
};

type ProductUsageReportEntry = {
  kind: 'dish' | 'semi' | 'writeoff' | string;
  documentId: number;
  documentName: string;
  date: string;
  quantity: number;
  orderLabel: string;
};

const emptyForm: ProductForm = {
  name: '', b52Code: '', b52Name: '', price: '', criticalStockThreshold: '0', weightType: 'kg',
  categoryId: '', supplierId: '', description: '', isActive: true, allergenIds: [] as number[], baseFoodId: '',
};

const ProductsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const operatingGroup = useSelectedOperatingGroup();
  const username = useAppSelector((s) => s.auth.username);
  const { products, categories, loading } = useAppSelector((s) => s.product);
  const { suppliers, supplierProducts, groupId, epoch, isCurrent } = useSupplierDataScope();
  const dishes = useAppSelector((s) => s.dish.items);
  const semiFinished = useAppSelector((s) => s.semiFinished.items);

  const [modalOpen, setModalOpen] = useState(false);
  const [b52ModalOpen, setB52ModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ProductForm>({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<ProductSort>('name-asc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const { preferences: tablePreferences, setWidth, setVisible, setDensity, reset: resetTablePreferences } =
    useTablePreferences(PRODUCT_TABLE_PREFERENCES_KEY, username, PRODUCT_TABLE_COLUMNS, LEGACY_PRODUCT_COLUMN_WIDTHS_KEY);
  const [baseFoods, setBaseFoods] = useState<BaseFood[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replaceSource, setReplaceSource] = useState<Product | null>(null);
  const [replaceTargetId, setReplaceTargetId] = useState('');
  const [deleteReplaceTargetId, setDeleteReplaceTargetId] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateMessage, setRecalculateMessage] = useState<string | null>(null);
  const [syncingSuppliers, setSyncingSuppliers] = useState(false);
  const [receiptRangeFrom, setReceiptRangeFrom] = useState('');
  const [receiptRangeTo, setReceiptRangeTo] = useState('');
  const [productDetailsModal, setProductDetailsModal] = useState<ProductDetailsModalState | null>(null);
  const [deleteBlockModal, setDeleteBlockModal] = useState<DeleteBlockModalState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchProductCategories());
    dispatch(fetchSemiFinished());
    dispatch(fetchDishes());
    api.get<BaseFood[]>('/nutrition/base-foods', { params: { includeInactive: true } })
      .then((res) => setBaseFoods(res.data))
      .catch(() => setBaseFoods([]));
  }, [dispatch]);

  useEffect(() => {
    if (!isCurrent || groupId === null) return;
    dispatch(fetchSuppliers({ groupId, epoch }));
    dispatch(fetchSupplierProducts({ groupId, epoch }));
  }, [dispatch, epoch, groupId, isCurrent]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const syncSuppliersFromReceipts = async () => {
    if (!isCurrent || groupId === null) return;
    if (!window.confirm('Оновити основного постачальника для всіх товарів за історією накладних та синхронізувати ціни в прайс-листах? Ручні прив’язки товарів із накладними будуть замінені.')) return;
    setSyncingSuppliers(true);
    setError(null);
    try {
      const response = await api.post<{ products: number }>('/products/sync-suppliers-from-receipts');
      await Promise.all([
        dispatch(fetchProducts()).unwrap(),
        dispatch(fetchSupplierProducts({ groupId, epoch })).unwrap(),
      ]);
      setRecalculateMessage(`Постачальників і прайс-листи оновлено за накладними (${response.data.products} товарів).`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося оновити постачальників за накладними.');
    } finally {
      setSyncingSuppliers(false);
    }
  };

  const extractError = (err: any, fallback: string) =>
    err?.response?.data?.message
    || (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors.join(' ') : null)
    || err?.message
    || fallback;

  const extractDeleteReferences = (err: any): ProductDeleteReference[] => {
    const rows = Array.isArray(err?.references)
      ? err.references
      : err?.response?.data?.references;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((row): ProductDeleteReference | null => {
        if (!row || typeof row !== 'object') return null;
        const documentId = Number(row.documentId);
        const lineId = Number(row.lineId);
        if (!Number.isFinite(documentId) || !Number.isFinite(lineId)) return null;
        return {
          scope: String(row.scope || ''),
          documentId,
          documentLabel: String(row.documentLabel || ''),
          date: row.date ? String(row.date) : null,
          lineId,
        };
      })
      .filter((row): row is ProductDeleteReference => row !== null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setError(null);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditingId(p.id);
    setError(null);
    setForm({
      name: p.name,
      b52Code: p.b52Code ?? '',
      b52Name: p.b52Name ?? '',
      price: String(p.price),
      criticalStockThreshold: String(p.criticalStockThreshold ?? 0),
      weightType: p.weightType,
      categoryId: p.categoryId != null ? String(p.categoryId) : '',
      supplierId: p.supplierId != null ? String(p.supplierId) : '',
      description: p.description,
      isActive: p.isActive,
      allergenIds: [],
      baseFoodId: p.baseFoodId != null ? String(p.baseFoodId) : '',
    });
    setModalOpen(true);
  };

  const buildPayload = (form: ProductForm) => ({
    name: form.name.trim(),
    b52Code: form.b52Code.trim(),
    b52Name: form.b52Name.trim(),
    price: parseFloat(form.price),
    criticalStockThreshold: parseFloat(form.criticalStockThreshold || '0'),
    weightType: form.weightType,
    categoryId: form.categoryId ? parseInt(form.categoryId, 10) : null,
    supplierId: form.supplierId ? parseInt(form.supplierId, 10) : null,
    description: form.description,
    isActive: form.isActive,
    allergenIds: form.allergenIds,
    baseFoodId: form.baseFoodId ? parseInt(form.baseFoodId, 10) : null,
  });

  const validateForm = (form: ProductForm) => {
    if (!form.name.trim()) return "Назва є обов'язковою";
    if (!form.price) return "Ціна є обов'язковою";
    if (Number.isNaN(parseFloat(form.price))) return 'Ціна має бути числом';
    if (parseFloat(form.price) < 0) return 'Ціна не може бути відʼємною';
    if (!Number.isFinite(Number(form.criticalStockThreshold)) || Number(form.criticalStockThreshold) < 0)
      return 'Критичний залишок має бути невідʼємним числом';
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editingId) {
        await dispatch(updateProduct({ id: editingId, data: buildPayload(form) })).unwrap();
      } else {
        await dispatch(createProduct(buildPayload(form))).unwrap();
      }
      await dispatch(fetchProducts());
      closeModal();
    } catch (err: any) {
      setError(extractError(err, editingId ? 'Не вдалося оновити товар' : 'Не вдалося створити товар'));
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculatePrices = async () => {
    try {
      setRecalculating(true);
      setRecalculateMessage('Аналізую зміни за трьома останніми накладними...');
      const preview = await api.get<{
        items: Array<{ id: number; name: string; currentPrice: number; newPrice: number | null; hasChange: boolean; receiptsUsed: number }>;
      }>('/products/recalculate-prices-preview', { params: { lastReceiptsCount: 3 } });
      const changedItems = preview.data.items.filter(item => item.hasChange);
      const examples = changedItems.slice(0, 5)
        .map(item => `${item.name}: ${item.currentPrice.toFixed(2)} → ${item.newPrice?.toFixed(2)} ₴`)
        .join('\n');
      const summary = changedItems.length
        ? `Зміниться цін: ${changedItems.length} з ${preview.data.items.length} товарів, для яких є історія накладних.${examples ? `\n\n${examples}` : ''}${changedItems.length > 5 ? '\n…' : ''}\n\nЦіни розраховуються з урахуванням ПДВ. Продовжити?`
        : `Ціни вже актуальні для всіх ${preview.data.items.length} товарів з історією накладних. Перерахувати повторно?`;
      if (!window.confirm(summary)) {
        setRecalculateMessage(null);
        return;
      }
      const result = await api.post<{ updated: number; message: string }>('/products/recalculate-prices-apply', {
        lastReceiptsCount: 3,
      });
      setRecalculateMessage(`✅ ${result.data.message} Залежні собівартості рецептів оновлено в межах тієї самої операції.`);
      try {
        await dispatch(fetchProducts()).unwrap();
      } catch {
        setRecalculateMessage(`✅ ${result.data.message} Залежні собівартості рецептів оновлено в межах тієї самої операції. Не вдалося оновити список — перезавантажте сторінку.`);
      }
    } catch (err: any) {
      setRecalculateMessage(extractError(err, 'Не вдалося перерахувати ціни'));
    } finally {
      setRecalculating(false);
    }
  };

  const handleApplyProductDetailsRange = async () => {
    if (!productDetailsModal) return;
    await reloadProductDetailData(productDetailsModal.product);
  };

  const handleRebuildCompletedOrderReport = async () => {
    if (productDetailsModal) {
      await reloadProductDetailData(productDetailsModal.product);
    }
    setRecalculateMessage('Звіт оновлено з поточних даних.');
  };

  const handleDelete = async (id: number) => {
    const product = products.find((p) => p.id === id);
    const usageEntries = getProductUsageEntries(id);
    if (usageEntries.length > 0) {
      openDeleteBlockModal(
        product?.name ?? `ID ${id}`,
        'Спроба видалити товар, який використовується в інших сутностях.',
        usageEntries,
        id
      );
      return;
    }
    if (!window.confirm('Видалити продукт?')) return;
    try {
      setError(null);
      await dispatch(deleteProduct(id)).unwrap();
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err: any) {
      const message = extractError(err, 'Не вдалося видалити товар');
      openDeleteBlockModal(product?.name ?? `ID ${id}`, message, usageEntries, id, extractDeleteReferences(err));
      setError(message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setError('Спочатку виберіть товари для видалення.');
      return;
    }
    if (!window.confirm(`Видалити вибрані товари (${selectedIds.length})?`)) return;

    const blockedLocal = selectedIds
      .map((id) => ({ id, product: products.find((p) => p.id === id), entries: getProductUsageEntries(id) }))
      .find((row) => row.entries.length > 0);
    if (blockedLocal) {
      openDeleteBlockModal(
        blockedLocal.product?.name ?? `ID ${blockedLocal.id}`,
        `Спроба масового видалення. Товар входить у страви/Н-Ф і не може бути видалений.`,
        blockedLocal.entries,
        blockedLocal.id
      );
      return;
    }

    try {
      setError(null);
      const result = await dispatch(bulkDeleteProducts(selectedIds)).unwrap();
      setSelectedIds((prev) => prev.filter((id) => !result.deletedIds.includes(id)));
      if (result.errors.length > 0) {
        const blockedId = selectedIds.find((id) => !result.deletedIds.includes(id));
        if (blockedId != null) {
          const product = products.find((p) => p.id === blockedId);
          openDeleteBlockModal(
            product?.name ?? `ID ${blockedId}`,
            result.errors.join(' '),
            getProductUsageEntries(blockedId),
            blockedId
          );
        }
        setError(result.errors.join(' '));
      }
    } catch (err: any) {
      const message = extractError(err, 'Не вдалося видалити вибрані товари');
      const blockedId = selectedIds[0];
      const product = blockedId != null ? products.find((p) => p.id === blockedId) : null;
      openDeleteBlockModal(
        product?.name ?? 'товар',
        message,
        blockedId != null ? getProductUsageEntries(blockedId) : [],
        blockedId
      );
      setError(message);
    }
  };

  const buildDishPayload = (dish: Dish, ingredients = dish.ingredients) => ({
    name: dish.name,
    b52Code: dish.b52Code ?? '',
    b52Name: dish.b52Name ?? '',
    categoryId: dish.categoryId,
    description: dish.description,
    cookingTime: dish.cookingTime,
    technologyDescription: dish.technologyDescription,
    inventoryRequired: dish.inventoryRequired,
    photoUrl: dish.photoUrl,
    netWeight: dish.netWeight,
    grossWeight: dish.grossWeight,
    cleanOutput: dish.cleanOutput,
    marginType: dish.marginType,
    margin: dish.margin,
    ingredients: ingredients.map((ingredient) => ({
      productId: ingredient.productId,
      semiFinishedProductId: ingredient.semiFinishedProductId,
      quantity: ingredient.quantity,
      bruttoWeight: ingredient.bruttoWeight,
      nettoWeight: ingredient.nettoWeight,
      piecesPerPortion: ingredient.piecesPerPortion ?? null,
      output: ingredient.output,
      notes: ingredient.notes ?? '',
    })),
  });

  const buildSemiFinishedPayload = (item: SemiFinishedProduct, rows = item.items) => ({
    name: item.name,
    b52Code: item.b52Code ?? '',
    b52Name: item.b52Name ?? '',
    categoryId: item.categoryId,
    description: item.description,
    weightType: item.weightType,
    netWeight: item.netWeight,
    grossWeight: item.grossWeight,
    excludeFromDishWeightCalc: item.excludeFromDishWeightCalc,
    isAuxiliaryRecipe: item.isAuxiliaryRecipe ?? false,
    items: rows.map((row) => ({
      productId: row.productId,
      subSemiFinishedProductId: row.subSemiFinishedProductId,
      quantity: row.quantity,
      bruttoWeight: row.bruttoWeight,
      nettoWeight: row.nettoWeight,
      output: row.output,
      notes: row.notes ?? '',
    })),
  });

  const getProductUsageEntries = (productId: number): UsageEntry[] => {
    const getUsedQuantity = (bruttoWeight: number, quantity: number) => {
      if (Number.isFinite(bruttoWeight) && bruttoWeight > 0) return bruttoWeight;
      if (Number.isFinite(quantity) && quantity > 0) return quantity;
      return 0;
    };

    const semiEntries = semiFinished
      .map((item) => ({
        kind: 'semi' as const,
        id: item.id,
        name: item.name,
        quantity: item.items
          .filter((row) => row.productId === productId)
          .reduce((sum, row) => sum + getUsedQuantity(row.bruttoWeight, row.quantity), 0),
      }))
      .filter((entry) => entry.quantity > 0);

    const dishEntries = dishes
      .map((dish) => ({
        kind: 'dish' as const,
        id: dish.id,
        name: dish.name,
        quantity: dish.ingredients
          .filter((ingredient) => ingredient.productId === productId)
          .reduce((sum, ingredient) => sum + getUsedQuantity(ingredient.bruttoWeight, ingredient.quantity), 0),
      }))
      .filter((entry) => entry.quantity > 0);

    return [...dishEntries, ...semiEntries].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'dish' ? -1 : 1;
      return a.name.localeCompare(b.name, 'uk');
    });
  };

  const openProductDetailsModal = (product: Product) => {
    const usageEntries = getProductUsageEntries(product.id);
    const linkedSupplierProducts = supplierProducts
      .filter((sp) => sp.internalProductId === product.id && sp.isActive)
      .sort((a, b) => a.effectiveUnitPrice - b.effectiveUnitPrice);
    const defaultFrom = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const defaultTo = new Date().toISOString().slice(0, 10);
    const from = receiptRangeFrom || defaultFrom;
    const to = receiptRangeTo || defaultTo;
    setProductDetailsModal({
      product,
      baseFood: null,
      baseFoodLoading: !!product.baseFoodId,
      baseFoodError: null,
      usageEntries,
      linkedSupplierProducts,
      receiptEntries: [],
      usageReportEntries: [],
      activeTab: 'usage',
      receiptsLoading: true,
      receiptsError: null,
    });

    if (product.baseFoodId) {
      api.get<BaseFood>(`/nutrition/base-foods/${product.baseFoodId}`)
        .then((res) => {
          setProductDetailsModal((prev) => {
            if (!prev || prev.product.id !== product.id) return prev;
            return { ...prev, baseFood: res.data, baseFoodLoading: false, baseFoodError: null };
          });
        })
        .catch((err: any) => {
          setProductDetailsModal((prev) => {
            if (!prev || prev.product.id !== product.id) return prev;
            return {
              ...prev,
              baseFood: null,
              baseFoodLoading: false,
              baseFoodError: extractError(err, 'Не вдалося завантажити нутрієнтну базу товару'),
            };
          });
        });
    }

    const params = { from, to };
    Promise.all([
      api.get<ProductReceiptEntry[]>(`/products/${product.id}/receipts`, { params }),
      api.get<ProductUsageReportEntry[]>(`/products/${product.id}/usage-report`, { params }),
    ])
      .then(([receiptRes, usageRes]) => {
        const filteredReceipts = (Array.isArray(receiptRes.data) ? receiptRes.data : []).filter((entry) => {
          const entryDate = new Date(entry.date).toISOString().slice(0, 10);
          return entryDate >= from && entryDate <= to;
        });
        setProductDetailsModal((prev) => {
          if (!prev || prev.product.id !== product.id) return prev;
          return {
            ...prev,
            receiptEntries: filteredReceipts,
            usageReportEntries: Array.isArray(usageRes.data) ? usageRes.data : [],
            receiptsLoading: false,
            receiptsError: null,
          };
        });
      })
      .catch((err: any) => {
        const message = extractError(err, 'Не вдалося завантажити приходи');
        setProductDetailsModal((prev) => {
          if (!prev || prev.product.id !== product.id) return prev;
          return {
            ...prev,
            receiptsLoading: false,
            receiptsError: message,
          };
        });
      });
  };

  const printProductDetailsModal = (modal: ProductDetailsModalState) => {
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const unitLabel = WEIGHT_TYPES.find((w) => w.value === modal.product.weightType)?.label ?? modal.product.weightType;
    const linkedRowsHtml = modal.linkedSupplierProducts.length === 0
      ? '<tr><td colspan="3" style="color:#7f8c8d;">Немає активних привʼязок.</td></tr>'
      : modal.linkedSupplierProducts.map((sp) => `
          <tr>
            <td>${escapeHtml(sp.supplierName || '—')}</td>
            <td>${escapeHtml(sp.supplierProductName || '—')}</td>
            <td style="text-align:right;">${sp.effectiveUnitPrice.toFixed(2)} ₴</td>
          </tr>
        `).join('');
    const usageRowsHtml = modal.usageEntries.length === 0
      ? '<tr><td colspan="4" style="color:#7f8c8d;">Немає входжень у стравах або напівфабрикатах.</td></tr>'
      : modal.usageEntries.map((entry) => `
          <tr>
            <td>${entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
            <td>${entry.id}</td>
            <td>${escapeHtml(entry.name)}</td>
            <td style="text-align:right;">${entry.quantity.toFixed(4)}</td>
          </tr>
        `).join('');
    const receiptRowsHtml = modal.receiptEntries.length === 0
      ? '<tr><td colspan="7" style="color:#7f8c8d;">Немає приходів у вибраному діапазоні.</td></tr>'
      : modal.receiptEntries.map((entry) => `
          <tr>
            <td>${new Date(entry.date).toLocaleDateString('uk-UA')}</td>
            <td>${escapeHtml(entry.invoiceNumber || '—')}</td>
            <td>${escapeHtml(entry.supplierName || '—')}</td>
            <td style="text-align:right;">${entry.quantity.toFixed(4)}</td>
            <td style="text-align:right;">${entry.unitPrice.toFixed(2)} ₴</td>
            <td style="text-align:right;">${entry.vatAmount.toFixed(2)} ₴</td>
            <td style="text-align:right;">${entry.lineTotal.toFixed(2)} ₴</td>
          </tr>
        `).join('');
    const usageReportRowsHtml = modal.usageReportEntries.length === 0
      ? '<tr><td colspan="5" style="color:#7f8c8d;">Немає даних використання у вибраному діапазоні.</td></tr>'
      : modal.usageReportEntries.map((entry) => `
          <tr>
            <td>${new Date(entry.date).toLocaleDateString('uk-UA')}</td>
            <td>${escapeHtml(entry.orderLabel || '—')}</td>
            <td>${escapeHtml(entry.documentName || '—')}</td>
            <td>${escapeHtml(entry.kind)}</td>
            <td style="text-align:right;">${entry.quantity.toFixed(4)}</td>
          </tr>
        `).join('');
    const baseFood = modal.baseFood;
    const nutrientRowsHtml = !baseFood || baseFood.nutrients.length === 0
      ? '<tr><td colspan="4" style="color:#7f8c8d;">Додаткові нутрієнти не заповнені.</td></tr>'
      : baseFood.nutrients.map((n) => `
         <tr>
           <td>${escapeHtml(n.group || 'Other')}</td>
           <td>${escapeHtml(n.name)}</td>
           <td style="text-align:right;">${n.amountPer100g.toFixed(3)}</td>
           <td>${escapeHtml(n.unit || '')}</td>
         </tr>
        `).join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Деталізація товару: ${escapeHtml(modal.product.name)}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1f2937;}
        h1,h2{margin:0 0 10px;}
        .meta{font-size:13px;color:#34495e;margin:2px 0;}
        table{width:100%;border-collapse:collapse;margin:10px 0 16px;}
        th,td{border:1px solid #d1d5db;padding:7px 8px;font-size:12px;vertical-align:top;}
        th{background:#ecf0f1;text-align:left;}
        @media print { body { print-color-adjust:exact; -webkit-print-color-adjust:exact; } body * { print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
      </style></head><body>
      <h1>Товар: ${escapeHtml(modal.product.name)}</h1>
      <div class="meta">Ціна: <strong>${modal.product.price.toFixed(2)} ₴</strong></div>
      <div class="meta">Одиниця: <strong>${escapeHtml(unitLabel)}</strong></div>
      <div class="meta">Категорія: <strong>${escapeHtml(modal.product.categoryName || '—')}</strong></div>
      <div class="meta">Постачальник: <strong>${escapeHtml(modal.product.supplierName || '—')}</strong></div>
      <div class="meta">Алергени з нутрієнтної бази: <strong>${escapeHtml(modal.product.allergens.length > 0 ? modal.product.allergens.map((a) => a.name).join(', ') : '—')}</strong></div>
      <div class="meta">Нутрієнтна база: <strong>${escapeHtml(modal.product.baseFoodName || '—')}</strong></div>
      ${(modal.product.b52Code || modal.product.b52Name) ? `<div class="meta">Синхронізація B52: <strong>${escapeHtml([modal.product.b52Code ? `Код: ${modal.product.b52Code}` : '', modal.product.b52Name ? `Назва: ${modal.product.b52Name}` : ''].filter(Boolean).join(' · '))}</strong></div>` : ''}
      ${modal.product.description?.trim() ? `<div class="meta">Опис: <strong>${escapeHtml(modal.product.description)}</strong></div>` : ''}
      ${baseFood ? `
      <h2>Харчова цінність з нутрієнтної бази / 100 г</h2>
      <table>
        <thead><tr><th>Ккал</th><th>Білки</th><th>Жири</th><th>Вуглеводи</th><th>Клітковина</th><th>Вода</th><th>Зола</th></tr></thead>
        <tbody><tr>
          <td style="text-align:right;">${baseFood.energyKcal.toFixed(2)}</td>
          <td style="text-align:right;">${baseFood.protein.toFixed(3)} г</td>
          <td style="text-align:right;">${baseFood.fat.toFixed(3)} г</td>
          <td style="text-align:right;">${baseFood.carbohydrates.toFixed(3)} г</td>
          <td style="text-align:right;">${baseFood.fiber.toFixed(3)} г</td>
          <td style="text-align:right;">${baseFood.water.toFixed(3)} г</td>
          <td style="text-align:right;">${baseFood.ash.toFixed(3)} г</td>
        </tr></tbody>
      </table>
      <h2>Вітаміни, мікро- та макроелементи / 100 г</h2>
      <table>
        <thead><tr><th>Група</th><th>Нутрієнт</th><th>Кількість</th><th>Од.</th></tr></thead>
        <tbody>${nutrientRowsHtml}</tbody>
      </table>` : ''}
      <h2>Привязка товару</h2>
      <table>
        <thead><tr><th>Постачальник</th><th>Назва</th><th>Ціна</th></tr></thead>
        <tbody>${linkedRowsHtml}</tbody>
      </table>
      <h2>Входження у стравах та Н/Ф</h2>
      <table>
        <thead><tr><th>Тип</th><th>ID</th><th>Назва</th><th>Кількість продукту</th></tr></thead>
        <tbody>${usageRowsHtml}</tbody>
      </table>
      <h2>Приходи</h2>
      <table>
        <thead><tr><th>Дата</th><th>Накладна</th><th>Постачальник</th><th>Кількість</th><th>Ціна</th><th>ПДВ</th><th>Разом</th></tr></thead>
        <tbody>${receiptRowsHtml}</tbody>
      </table>
      <h2>Звіт по реалізації продукту</h2>
      <table>
        <thead><tr><th>Дата</th><th>Замовлення</th><th>Документ</th><th>Тип</th><th>Кількість</th></tr></thead>
        <tbody>${usageReportRowsHtml}</tbody>
      </table>
      </body></html>`;

    void openAndPrintHtml(html, { windowFeatures: 'width=1100,height=800', minimumDelayMs: 300 });
  };

  const reloadProductDetailData = async (product: Product) => {
    const from = receiptRangeFrom || new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const to = receiptRangeTo || new Date().toISOString().slice(0, 10);
    setProductDetailsModal((prev) => prev && prev.product.id === product.id ? { ...prev, receiptsLoading: true, receiptsError: null } : prev);
    try {
      const params = { from, to };
      const [receiptRes, usageRes] = await Promise.all([
        api.get<ProductReceiptEntry[]>(`/products/${product.id}/receipts`, { params }),
        api.get<ProductUsageReportEntry[]>(`/products/${product.id}/usage-report`, { params }),
      ]);
      setProductDetailsModal((prev) => {
        if (!prev || prev.product.id !== product.id) return prev;
        return {
          ...prev,
          receiptEntries: (Array.isArray(receiptRes.data) ? receiptRes.data : []).filter((entry) => {
            const entryDate = new Date(entry.date).toISOString().slice(0, 10);
            return entryDate >= from && entryDate <= to;
          }),
          usageReportEntries: Array.isArray(usageRes.data) ? usageRes.data : [],
          receiptsLoading: false,
          receiptsError: null,
        };
      });
    } catch (err: any) {
      setProductDetailsModal((prev) => {
        if (!prev || prev.product.id !== product.id) return prev;
        return { ...prev, receiptsLoading: false, receiptsError: extractError(err, 'Не вдалося завантажити дані') };
      });
    }
  };

  const openDeleteBlockModal = (
    productName: string,
    serverMessage: string,
    entries: UsageEntry[],
    productId?: number,
    references: ProductDeleteReference[] = []
  ) => {
    setDeleteBlockModal({
      title: productId != null
        ? `Неможливо видалити товар "${productName}" (ID: ${productId})`
        : `Неможливо видалити товар "${productName}"`,
      message: serverMessage,
      entries,
      references,
      productId,
    });
    setDeleteReplaceTargetId('');
  };

  const getDeleteReferenceTypeLabel = (scope: string) => {
    switch (scope) {
      case 'inventory-receipt':
        return 'Накладна';
      case 'inventory-revision':
        return 'Ревізія';
      case 'inventory-writeoff':
        return 'Списання';
      case 'purchase-worksheet':
        return 'Закупівельний лист';
      default:
        return scope || 'Документ';
    }
  };

  const executeProductReplacement = async (source: Product, target: Product) => {
    const operationGroupId = operatingGroup.groupId;
    const ensureReplacementAvailable = () => {
      if (operationGroupId === null && operatingGroup.isCurrent(operationGroupId)) return;
      throw new Error(getHistoricalWriteOffUnavailableNotice(
        operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
        || 'Робочий контекст змінився; заміну товару не продовжено.');
    };
    ensureReplacementAvailable();
    const affectedSemiFinished = semiFinished.filter((item) => item.items.some((row) => row.productId === source.id));
    const affectedDishes = dishes.filter((dish) => dish.ingredients.some((ingredient) => ingredient.productId === source.id));
    const totalRecipeMatches = affectedSemiFinished.length + affectedDishes.length;
    let updatedRecipeCount = 0;
    let replaceWriteOffResponse;

    try {
      for (const item of affectedSemiFinished) {
        ensureReplacementAvailable();
        const nextRows = item.items.map((row) => row.productId === source.id
          ? { ...row, productId: target.id }
          : row);
        await dispatch(updateSemiFinished({ id: item.id, data: buildSemiFinishedPayload(item, nextRows) })).unwrap();
        updatedRecipeCount++;
      }

      for (const dish of affectedDishes) {
        ensureReplacementAvailable();
        const nextIngredients = dish.ingredients.map((ingredient) => ingredient.productId === source.id
          ? { ...ingredient, productId: target.id }
          : ingredient);
        await dispatch(updateDish({ id: dish.id, data: buildDishPayload(dish, nextIngredients) })).unwrap();
        updatedRecipeCount++;
      }

      ensureReplacementAvailable();
      replaceWriteOffResponse = await api.post<{
        updatedItemCount: number;
        updatedWriteOffCount: number;
        onlyAutoWriteOffs: boolean;
      }>('/inventorywriteoffs/replace-product', {
        sourceProductId: source.id,
        targetProductId: target.id,
        onlyAutoWriteOffs: true,
      });
    } catch (err: any) {
      if (updatedRecipeCount > 0) {
        throw new Error(`Заміна зупинена після оновлення ${updatedRecipeCount} рецептур. Подальший крок не підтверджено: ${extractError(err, 'невідома помилка')}`);
      }
      const status = Number(err?.response?.status || 0);
      if (status === 404 || status === 405) {
        throw new Error('Потрібно перезапустити backend: сервіс заміни в автосписаннях ще не активовано.');
      }
      throw err;
    }

    const updatedWriteOffItemCount = Number(replaceWriteOffResponse.data?.updatedItemCount || 0);
    const updatedWriteOffCount = Number(replaceWriteOffResponse.data?.updatedWriteOffCount || 0);

    await Promise.all([
      dispatch(fetchProducts()),
      dispatch(fetchSemiFinished()),
      dispatch(fetchDishes()),
    ]);

    return {
      totalRecipeMatches,
      updatedWriteOffItemCount,
      updatedWriteOffCount,
    };
  };

  const openReplaceModal = (product: Product) => {
    if (!operatingGroup.isCurrent(operatingGroup.groupId) || !canReplaceProducts) {
      setError(getHistoricalWriteOffUnavailableNotice(
        operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
        || 'Робочий контекст змінився; заміну товару не розпочато.');
      return;
    }
    setReplaceSource(product);
    setReplaceTargetId('');
    setError(null);
  };

  const closeReplaceModal = () => {
    setReplaceSource(null);
    setReplaceTargetId('');
    setReplacing(false);
  };

  const handleReplaceProduct = async () => {
    if (!replaceSource) return;
    if (!operatingGroup.isCurrent(operatingGroup.groupId) || !canReplaceProducts) {
      setError(getHistoricalWriteOffUnavailableNotice(
        operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
        || 'Робочий контекст змінився; заміну товару не розпочато.');
      return;
    }
    const targetIdNum = parseInt(replaceTargetId, 10);
    if (!targetIdNum) {
      setError('Оберіть товар, на який потрібно замінити.');
      return;
    }

    const target = products.find((product) => product.id === targetIdNum);
    if (!target) {
      setError('Товар для заміни не знайдено.');
      return;
    }

    if (!window.confirm(`Замінити "${replaceSource.name}" на "${target.name}" у стравах, Н/Ф та автосписаннях по замовленнях?`)) return;

    try {
      setReplacing(true);
      setError(null);
      const { totalRecipeMatches, updatedWriteOffItemCount, updatedWriteOffCount } = await executeProductReplacement(replaceSource, target);

      if (totalRecipeMatches === 0 && updatedWriteOffItemCount === 0) {
        setError(`Товар "${replaceSource.name}" не знайдено у стравах, Н/Ф або автосписаннях по замовленнях.`);
        setReplacing(false);
        return;
      }

      const successParts: string[] = [];
      if (totalRecipeMatches > 0) {
        successParts.push(`рецептурні заміни: ${totalRecipeMatches}`);
      }
      if (updatedWriteOffItemCount > 0) {
        successParts.push(`автосписання: ${updatedWriteOffItemCount} рядків у ${updatedWriteOffCount} документах`);
      }
      if (successParts.length > 0) {
        window.alert(`Заміна виконана: ${successParts.join('; ')}.`);
      }
      closeReplaceModal();
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося виконати масову заміну товару'));
      setReplacing(false);
    }
  };

  const handleReplaceFromDeleteModal = async () => {
    if (!deleteBlockModal?.productId) return;
    if (!operatingGroup.isCurrent(operatingGroup.groupId) || !canReplaceProducts) {
      setError(getHistoricalWriteOffUnavailableNotice(
        operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
        || 'Робочий контекст змінився; заміну товару не розпочато.');
      return;
    }

    const source = products.find((product) => product.id === deleteBlockModal.productId);
    if (!source) {
      setError('Товар для заміни не знайдено.');
      return;
    }

    const targetIdNum = parseInt(deleteReplaceTargetId, 10);
    if (!targetIdNum) {
      setError('Оберіть товар, на який потрібно замінити.');
      return;
    }

    const target = products.find((product) => product.id === targetIdNum);
    if (!target) {
      setError('Товар для заміни не знайдено.');
      return;
    }

    try {
      setReplacing(true);
      setError(null);
      await executeProductReplacement(source, target);
      if (!operatingGroup.isCurrent(operatingGroup.groupId)) {
        throw new Error('Заміна завершилася, але робочий контекст змінився. Товар не видалено; перевірте результат заміни перед продовженням.');
      }
      await dispatch(deleteProduct(source.id)).unwrap();
      setSelectedIds((prev) => prev.filter((x) => x !== source.id));
      setDeleteBlockModal(null);
      setDeleteReplaceTargetId('');
      window.alert(`Товар "${source.name}" замінено на "${target.name}" і видалено.`);
    } catch (err: any) {
      const message = extractError(err, 'Не вдалося замінити та видалити товар');
      const usageEntries = getProductUsageEntries(source.id);
      openDeleteBlockModal(source.name, message, usageEntries, source.id, extractDeleteReferences(err));
      setError(message);
      setReplacing(false);
    } finally {
      setReplacing(false);
    }
  };

  const normalizeLookup = (value: unknown) => String(value ?? '').trim().toLowerCase();

  const parseActiveFlag = (value: unknown, fallback: boolean) => {
    const normalized = normalizeLookup(value);
    if (!normalized) return fallback;
    return ['так', 'true', '1', 'yes', 'y'].includes(normalized);
  };

  const resolveExistingProduct = (row: any) => {
    const id = parseInt(String(row['Код (ID)'] ?? row['id'] ?? '').trim(), 10);
    const name = String(row['Назва продукту'] ?? row['name'] ?? '').trim();
    const b52Code = String(row['Код B52'] ?? row['b52Code'] ?? '').trim();

    return products.find((product) => b52Code && normalizeLookup(product.b52Code) === normalizeLookup(b52Code))
      ?? (Number.isInteger(id) && id > 0 ? products.find((product) => product.id === id) : undefined)
      ?? products.find((product) => name && normalizeLookup(product.name) === normalizeLookup(name));
  };

  // ── Excel Export ──────────────────────────────────────────────
  const handleExport = () => {
    const rows = products.map((p) => ({
      'Код (ID)': p.id,
      'Назва продукту': p.name,
      'Код B52': p.b52Code ?? '',
      'Назва B52': p.b52Name ?? '',
      'Ціна (₴)': p.price,
      'Одиниця виміру': WEIGHT_TYPES.find(w => w.value === p.weightType)?.label ?? p.weightType,
      'Категорія': p.categoryName ?? '',
      'Постачальник': p.supplierName ?? '',
      'Опис': p.description ?? '',
      'Активний': p.isActive ? 'Так' : 'Ні',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    // Auto column widths
    const colWidths = [
    { wch: 10 }, { wch: 30 }, { wch: 16 }, { wch: 30 }, { wch: 12 }, { wch: 14 },
      { wch: 20 }, { wch: 20 }, { wch: 30 }, { wch: 10 },
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Продукти');
    XLSX.writeFile(wb, `products_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Excel Import ──────────────────────────────────────────────
  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (rows.length === 0) { setImportError('Файл порожній або не містить даних.'); return; }

        let created = 0, updated = 0, skipped = 0;

        for (const row of rows) {
          const existing = resolveExistingProduct(row);
          const name = String(row['Назва продукту'] ?? row['name'] ?? '').trim();
          const priceRaw = row['Ціна (₴)'] ?? row['price'] ?? 0;
          const price = parseFloat(String(priceRaw));

          if (!name) { skipped++; continue; }
          if (isNaN(price) || price < 0) { skipped++; continue; }

          const weightLabel = String(row['Одиниця виміру'] ?? '').trim();
          const weightType = WEIGHT_TYPES.find(w => w.label === weightLabel || w.value === weightLabel)?.value ?? 'kg';

          const catName = String(row['Категорія'] ?? '').trim();
          const supName = String(row['Постачальник'] ?? '').trim();
          const categoryId = catName
            ? (categories.find(c => normalizeLookup(c.name) === normalizeLookup(catName))?.id ?? null)
            : (existing?.categoryId ?? null);
          const supplierId = supName
            ? (suppliers.find(s => normalizeLookup(s.name) === normalizeLookup(supName))?.id ?? null)
            : (existing?.supplierId ?? null);
          const payload = {
            name,
            b52Code: String(row['Код B52'] ?? row['b52Code'] ?? existing?.b52Code ?? '').trim(),
            b52Name: String(row['Назва B52'] ?? row['b52Name'] ?? existing?.b52Name ?? '').trim(),
            price,
            weightType,
            categoryId,
            supplierId,
            description: String(row['Опис'] ?? existing?.description ?? '').trim(),
            isActive: parseActiveFlag(row['Активний'], existing?.isActive ?? true),
            allergenIds: [],
          };

          try {
            if (existing) {
              await dispatch(updateProduct({ id: existing.id, data: payload })).unwrap();
              updated++;
            } else {
              await dispatch(createProduct(payload)).unwrap();
              created++;
            }
          } catch {
            skipped++;
          }
        }

        dispatch(fetchProducts());
        setImportError(`Імпортовано. Створено: ${created}. Оновлено: ${updated}. Пропущено: ${skipped}.`);
      } catch (err) {
        setImportError('Помилка читання файлу. Перевірте формат.');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const inputStyle: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '8px', width: '100%', boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
  const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--crm-text)', fontSize: '14px' };
  const cellStyle: React.CSSProperties = { padding: '10px 8px', verticalAlign: 'top' };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredProducts = products.filter((p) => {
    if (!normalizedSearch) return true;
    return p.name.toLowerCase().includes(normalizedSearch);
  });
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name-desc': return b.name.localeCompare(a.name, 'uk');
      case 'price-asc': return a.price - b.price;
      case 'price-desc': return b.price - a.price;
      case 'category-asc': return (a.categoryName ?? '').localeCompare(b.categoryName ?? '', 'uk');
      case 'category-desc': return (b.categoryName ?? '').localeCompare(a.categoryName ?? '', 'uk');
      case 'name-asc':
      default: return a.name.localeCompare(b.name, 'uk');
    }
  });
  const getSortArrow = (ascKey: ProductSort, descKey: ProductSort) => {
    if (sortBy === ascKey) return ' ↑';
    if (sortBy === descKey) return ' ↓';
    return '';
  };
  const toggleSort = (ascKey: ProductSort, descKey: ProductSort) => {
    setSortBy((prev) => (prev === ascKey ? descKey : ascKey));
  };

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pagedProducts = sortedProducts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageStart = sortedProducts.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, sortedProducts.length);
  const allVisibleSelected = pagedProducts.length > 0 && pagedProducts.every((p) => selectedIds.includes(p.id));

  const percentColumnKeys = PRODUCT_TABLE_COLUMNS.map(column => column.key);
  const totalConfiguredPercent = percentColumnKeys.reduce((sum, key) => sum + tablePreferences.widths[key], 0);
  const scale = totalConfiguredPercent > 95 ? 95 / totalConfiguredPercent : 1;
  const widthPct = (key: ProductColumnKey) => `${(tablePreferences.widths[key] * scale).toFixed(2)}%`;
  const visibleProductColumns = PRODUCT_TABLE_COLUMNS.filter(column => tablePreferences.visibility[column.key]);
  const weightTypeSearchItems = WEIGHT_TYPES.map((w) => ({ id: w.value, name: `${w.label} (${w.value})` }));
  const categorySearchItems = categories.map((c) => ({ id: String(c.id), name: c.name }));
  const supplierSearchItems = suppliers.map((s) => ({ id: String(s.id), name: s.name }));
  const baseFoodSearchItems = baseFoods.map((item) => ({ id: String(item.id), name: `${item.name}${item.category ? ` · ${item.category}` : ''}` }));
  const replaceProductSearchItems = products
    .filter((product) => product.id !== replaceSource?.id)
    .map((product) => ({ id: String(product.id), name: `${product.name} (${product.weightType})` }));
  const deleteReplaceSourceProduct = deleteBlockModal?.productId != null
    ? products.find((product) => product.id === deleteBlockModal.productId) ?? null
    : null;
  const deleteReplaceProductSearchItems = products
    .filter((product) => product.id !== deleteReplaceSourceProduct?.id)
    .map((product) => ({ id: String(product.id), name: `${product.name} (${product.weightType})` }));
  const replacementUnavailableNotice = getHistoricalWriteOffUnavailableNotice(
    operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready);
  const canReplaceProducts = replacementUnavailableNotice === null;

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>Управління товарами</h2>

      {/* Import / Export toolbar */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={handleExport}
          style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          ⬇ Експорт Excel
        </button>

        <label style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: importing ? 'not-allowed' : 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', opacity: importing ? 0.7 : 1 }}>
          ⬆ {importing ? 'Імпорт...' : 'Імпорт Excel'}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportFile}
            disabled={importing}
            style={{ display: 'none' }}
          />
        </label>

        <button
          onClick={() => setB52ModalOpen(true)}
          style={{ background: 'var(--crm-blocked-bg)', color: 'var(--crm-blocked)', border: '1px solid var(--crm-blocked)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: 'pointer', fontWeight: '500' }}
        >
          B52 Довідник
        </button>

        <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
          Колонки імпорту: <strong>Назва продукту</strong>, <strong>Ціна (₴)</strong>, Одиниця виміру, Категорія, Постачальник, Опис, Активний
        </span>
      </div>

      {importError && (
        <div className={`crm-import-result ${importError.startsWith('Імпортовано') ? 'crm-import-result--success' : 'crm-import-result--error'}`} role={importError.startsWith('Імпортовано') ? 'status' : 'alert'}>
          {importError}
        </div>
      )}

      <TablePreferencesPanel
        columns={PRODUCT_TABLE_COLUMNS}
        preferences={tablePreferences}
        onWidthChange={setWidth}
        onVisibilityChange={setVisible}
        onDensityChange={setDensity}
        onReset={resetTablePreferences}
      />
      <div>
        {recalculateMessage && (
          <div role="status" aria-live="polite" style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', fontSize: '14px' }}>
            {recalculateMessage}
          </div>
        )}
      </div>
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={openNew}
          style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', border: '1px solid var(--crm-success)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: 'pointer', fontWeight: '500' }}
        >
          + Додати товар
        </button>

        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Пошук товару..."
          style={{ ...inputStyle, maxWidth: '320px' }}
        />
        <button
          type="button"
          onClick={handleBulkDelete}
          disabled={selectedIds.length === 0}
          style={{ background: selectedIds.length === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-danger-bg)', color: selectedIds.length === 0 ? 'var(--crm-text-muted)' : 'var(--crm-danger)', border: `1px solid ${selectedIds.length === 0 ? 'var(--crm-border)' : 'var(--crm-danger)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '500' }}
        >
          Видалити вибрані ({selectedIds.length})
        </button>
        <button
          type="button"
          onClick={handleRecalculatePrices}
          disabled={recalculating}
          style={{ background: recalculating ? 'var(--crm-surface-alt)' : 'var(--crm-info-bg)', color: recalculating ? 'var(--crm-text-muted)' : 'var(--crm-info)', border: `1px solid ${recalculating ? 'var(--crm-border)' : 'var(--crm-info)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: recalculating ? 'not-allowed' : 'pointer', fontWeight: '500' }}
        >
          {recalculating ? 'Перерахунок...' : 'Перерахувати ціни з ПДВ'}
        </button>
        <button
          type="button"
          onClick={syncSuppliersFromReceipts}
          disabled={syncingSuppliers}
          style={{ background: syncingSuppliers ? 'var(--crm-surface-alt)' : 'var(--crm-success-bg)', color: syncingSuppliers ? 'var(--crm-text-muted)' : 'var(--crm-success)', border: `1px solid ${syncingSuppliers ? 'var(--crm-border)' : 'var(--crm-success)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '8px 16px', cursor: syncingSuppliers ? 'not-allowed' : 'pointer', fontWeight: '500' }}
        >
          {syncingSuppliers ? 'Оновлення...' : 'Оновити постачальників за накладними'}
        </button>
      </div>

      {error && <div role="alert" style={{ marginTop: '10px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px', borderRadius: 'var(--crm-radius-sm)' }}>{error}</div>}
      {replacementUnavailableNotice && (
        <div role="note" style={{ marginTop: '10px', background: 'var(--crm-info-bg)', color: 'var(--crm-text)', border: '1px solid var(--crm-info)', padding: '10px', borderRadius: 'var(--crm-radius-sm)' }}>
          {replacementUnavailableNotice}
        </div>
      )}

      {/* Table */}
      {loading ? <p style={{ marginTop: '20px' }}>Завантаження...</p> : (
        <div>
          <div style={{ marginTop: '14px', fontSize: '13px', color: 'var(--crm-text-muted)' }}>
            Показано {pageStart}-{pageEnd} із {sortedProducts.length} товарів
          </div>
          <table className={`crm-table ${tablePreferences.density === 'compact' ? 'crm-table--compact' : ''}`} style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: 'var(--crm-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', marginTop: '8px' }}>
            <colgroup>
              <col style={{ width: '3%' }} />
              {visibleProductColumns.map(column => <col key={column.key} style={{ width: widthPct(column.key) }} />)}
            </colgroup>
            <thead>
              <tr>
                <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: '600', width: '3%' }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => {
                      const visibleIds = pagedProducts.map((p) => p.id);
                      setSelectedIds((prev) => e.target.checked
                        ? Array.from(new Set([...prev, ...visibleIds]))
                        : prev.filter((id) => !visibleIds.includes(id)));
                    }}
                  />
                </th>
                {tablePreferences.visibility.id && <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: '600', width: widthPct('id') }}>#</th>}
                <th
                  style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('name'), cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('name-asc', 'name-desc')}
                  title="Сортувати за назвою"
                >
                  Назва{getSortArrow('name-asc', 'name-desc')}
                </th>
                {tablePreferences.visibility.price && <th
                  style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('price'), cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('price-asc', 'price-desc')}
                  title="Сортувати за ціною"
                >
                  Ціна{getSortArrow('price-asc', 'price-desc')}
                </th>}
                {tablePreferences.visibility.unit && <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('unit') }}>Од.</th>}
                {tablePreferences.visibility.category && <th
                  style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('category'), cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => toggleSort('category-asc', 'category-desc')}
                  title="Сортувати за категорією"
                >
                  Категорія{getSortArrow('category-asc', 'category-desc')}
                </th>}
                {tablePreferences.visibility.supplier && <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('supplier') }}>Постачальник</th>}
                {tablePreferences.visibility.actions && <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: '600', width: widthPct('actions') }}>Дії</th>}
              </tr>
            </thead>
            <tbody>
              {pagedProducts.map((p) => {
                const isInactiveRow = !p.isActive;
                return (
                  <tr
                    key={p.id}
                    style={{
                      background: isInactiveRow ? 'var(--crm-surface-alt)' : undefined,
                      color: isInactiveRow ? 'var(--crm-text-muted)' : undefined,
                      fontStyle: isInactiveRow ? 'italic' : 'normal',
                    }}
                  >
                    <td style={{ ...cellStyle, width: '3%', padding: '6px 4px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, p.id] : prev.filter((id) => id !== p.id))}
                      />
                    </td>
                    {tablePreferences.visibility.id && <td style={{ ...cellStyle, width: widthPct('id'), padding: '8px 4px', textAlign: 'center' }}>{p.id}</td>}

                    {tablePreferences.visibility.name && <td style={{ ...cellStyle, width: widthPct('name') }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                        <button
                          type="button"
                          onClick={() => openProductDetailsModal(p)}
                          style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', fontWeight: 600, color: 'var(--crm-table-name-text)', cursor: 'pointer' }}
                          title={p.description?.trim() ? p.description : 'Опис відсутній'}
                        >
                          {p.name}
                        </button>
                        {(p.b52Code || p.b52Name) && (
                          <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                            {[p.b52Code ? `Код: ${p.b52Code}` : '', p.b52Name ? `Назва B52: ${p.b52Name}` : ''].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                    </td>}

                    {tablePreferences.visibility.price && <td style={{ ...cellStyle, width: widthPct('price') }}>
                      {p.price} ₴
                    </td>}

                    {tablePreferences.visibility.unit && <td style={{ ...cellStyle, width: widthPct('unit') }}>
                      {WEIGHT_TYPES.find(w => w.value === p.weightType)?.label ?? p.weightType}
                    </td>}

                    {tablePreferences.visibility.category && <td style={{ ...cellStyle, width: widthPct('category') }}>
                      {p.categoryName ?? '—'}
                    </td>}

                    {tablePreferences.visibility.supplier && <td style={{ ...cellStyle, width: widthPct('supplier') }}>
                      {p.supplierName ?? '—'}
                    </td>}

                    {tablePreferences.visibility.actions && <td style={{ ...cellStyle, width: widthPct('actions') }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => openEdit(p)}
                          style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}
                        >
                          Ред.
                        </button>
                        {canReplaceProducts && (
                          <button
                            type="button"
                            onClick={() => openReplaceModal(p)}
                            style={{ background: 'var(--crm-warning-bg)', color: 'var(--crm-warning)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            Заміна
                          </button>
                        )}
                        <button type="button" onClick={() => handleDelete(p.id)} style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', padding: '5px 8px', cursor: 'pointer', fontSize: '12px' }}>Вид.</button>
                      </div>
                    </td>}
                  </tr>
                );
              })}

              {pagedProducts.length === 0 && (
                <tr><td colSpan={visibleProductColumns.length + 1} style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Продукти не знайдені</td></tr>
              )}
            </tbody>
          </table>

          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              style={{ background: page <= 1 ? 'var(--crm-surface-alt)' : 'var(--crm-info-bg)', color: page <= 1 ? 'var(--crm-text-muted)' : 'var(--crm-info)', border: `1px solid ${page <= 1 ? 'var(--crm-border)' : 'var(--crm-info)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '6px 12px', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Назад
            </button>
            <span style={{ fontSize: '13px', color: 'var(--crm-text)' }}>Сторінка {page} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              style={{ background: page >= totalPages ? 'var(--crm-surface-alt)' : 'var(--crm-info-bg)', color: page >= totalPages ? 'var(--crm-text-muted)' : 'var(--crm-info)', border: `1px solid ${page >= totalPages ? 'var(--crm-border)' : 'var(--crm-info)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '6px 12px', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="products-edit-modal" style={{ width: '100%', maxWidth: '980px', maxHeight: '85vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <div style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', padding: '16px 20px', borderRadius: 'var(--crm-radius-lg) var(--crm-radius-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{editingId ? `Редагування: ${form.name}` : 'Новий товар'}</h3>
              <button type="button" onClick={closeModal} style={{ background: 'none', border: 'none', color: 'var(--crm-text)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div className="products-edit-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.6fr 1.3fr', gap: '12px' }}>
                <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
                  Назва *
                  <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Назва товару" />
                </label>
                <B52Autocomplete
                  b52Code={form.b52Code}
                  b52Name={form.b52Name}
                  onChange={(code, name) => setForm({ ...form, b52Code: code, b52Name: name })}
                  labelStyle={labelStyle}
                  inputStyle={inputStyle}
                />
                <label style={labelStyle}>
                  Ціна (₴) *
                  <input type="number" min="0" step="0.01" style={inputStyle} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" />
                </label>
                <label style={labelStyle}>
                  Критичний залишок ({WEIGHT_TYPES.find((unit) => unit.value === form.weightType)?.label ?? form.weightType})
                  <input type="number" min="0" step="0.001" style={inputStyle} value={form.criticalStockThreshold} onChange={(e) => setForm({ ...form, criticalStockThreshold: e.target.value })} placeholder="0 = сповіщати при нулі/мінусі" />
                </label>
                <label style={labelStyle}>
                  Одиниця виміру
                  <SearchSelect items={weightTypeSearchItems} value={form.weightType} onChange={(id) => setForm({ ...form, weightType: id || 'kg' })} placeholder="Пошук одиниці..." />
                </label>
                <label style={labelStyle}>
                  Категорія
                  <SearchSelect items={categorySearchItems} value={form.categoryId} onChange={(id) => setForm({ ...form, categoryId: id })} placeholder="Пошук категорії..." />
                </label>
                <label style={labelStyle}>
                  Постачальник
                  <SearchSelect items={supplierSearchItems} value={form.supplierId} onChange={(id) => setForm({ ...form, supplierId: id })} placeholder="Пошук постачальника..." />
                </label>
                <label style={{ ...labelStyle, gridColumn: 'span 2' }}>
                  Базовий продукт для нутрієнтів
                  <SearchSelect items={baseFoodSearchItems} value={form.baseFoodId} onChange={(id) => setForm({ ...form, baseFoodId: id })} placeholder="Пошук у нутрієнтній базі..." />
                </label>
              </div>
              <div style={{ ...labelStyle, background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 10 }}>
                <span>Алергени</span>
                <div style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>
                  Алергени товару тепер беруться тільки з обраного базового продукту нутрієнтної бази.
                </div>
              </div>
              <label style={labelStyle}>
                Опис
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Опис товару" />
              </label>
              <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                Активний
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={closeModal} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 16px', cursor: 'pointer' }}>
                  Скасувати
                </button>
                <button type="button" onClick={handleSave} disabled={saving} style={{ background: saving ? 'var(--crm-surface-alt)' : 'var(--crm-success-bg)', color: saving ? 'var(--crm-text-muted)' : 'var(--crm-success)', border: `1px solid ${saving ? 'var(--crm-border)' : 'var(--crm-success)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '10px 16px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  {saving ? 'Збереження...' : (editingId ? 'Зберегти' : 'Створити')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {replaceSource && canReplaceProducts && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '560px', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Масова заміна товару</h3>
              <button type="button" onClick={closeReplaceModal} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ color: 'var(--crm-text)', fontSize: '14px' }}>
              Замінити <strong>{replaceSource.name}</strong> у всіх стравах та напівфабрикатах на:
            </div>
            <SearchSelect
              items={replaceProductSearchItems}
              value={replaceTargetId}
              onChange={setReplaceTargetId}
              placeholder="Оберіть товар для заміни..."
              minWidth={260}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" onClick={closeReplaceModal} style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 16px', cursor: 'pointer' }}>
                Скасувати
              </button>
              <button type="button" disabled={replacing} onClick={handleReplaceProduct} style={{ background: replacing ? 'var(--crm-surface-alt)' : 'var(--crm-warning-bg)', color: replacing ? 'var(--crm-text-muted)' : 'var(--crm-warning)', border: `1px solid ${replacing ? 'var(--crm-border)' : 'var(--crm-warning)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '10px 16px', cursor: replacing ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {replacing ? 'Заміна...' : 'Замінити'}
              </button>
            </div>
          </div>
        </div>
      )}

      {productDetailsModal && (
        <div className="products-details-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="products-details-modal" style={{ width: '100%', maxWidth: '760px', maxHeight: '80vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="products-details-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Товар: {productDetailsModal.product.name}</h3>
              <div className="products-details-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => printProductDetailsModal(productDetailsModal)}
                  style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 12px', cursor: 'pointer', fontWeight: 600 }}
                >
                  🖨 Друк
                </button>
                <button type="button" onClick={() => setProductDetailsModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>

            <div className="products-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', color: 'var(--crm-text)' }}>
              <div>Ціна: <strong>{productDetailsModal.product.price} ₴</strong></div>
              <div>Одиниця: <strong>{WEIGHT_TYPES.find((w) => w.value === productDetailsModal.product.weightType)?.label ?? productDetailsModal.product.weightType}</strong></div>
              <div>Категорія: <strong>{productDetailsModal.product.categoryName ?? '—'}</strong></div>
              <div>Постачальник: <strong>{productDetailsModal.product.supplierName ?? '—'}</strong></div>
              <div style={{ gridColumn: '1 / -1' }}>Нутрієнтна база: <strong>{productDetailsModal.product.baseFoodName ?? '—'}</strong></div>
              <div style={{ gridColumn: '1 / -1' }}>
                Алергени з нутрієнтної бази:{' '}
                <strong>
                  {productDetailsModal.product.allergens.length > 0
                    ? productDetailsModal.product.allergens.map((a) => a.name).join(', ')
                    : '—'}
                </strong>
              </div>
              {(productDetailsModal.product.b52Code || productDetailsModal.product.b52Name) && (
                <div style={{ gridColumn: '1 / -1' }}>
                  Синхронізація B52:{' '}
                  <strong>
                    {[productDetailsModal.product.b52Code ? `Код: ${productDetailsModal.product.b52Code}` : '', productDetailsModal.product.b52Name ? `Назва: ${productDetailsModal.product.b52Name}` : ''].filter(Boolean).join(' · ')}
                  </strong>
                </div>
              )}
              {productDetailsModal.product.description?.trim() && (
                <div style={{ gridColumn: '1 / -1' }}>
                  Опис: <strong>{productDetailsModal.product.description}</strong>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '10px' }}>
              <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px' }}>Харчова цінність з нутрієнтної бази</div>
              {!productDetailsModal.product.baseFoodId ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>Товар ще не прив’язаний до базового продукту. Прив’язку можна зробити в редагуванні товару або на сторінці «Нутрієнтна база».</div>
              ) : productDetailsModal.baseFoodLoading ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження нутрієнтної інформації...</div>
              ) : productDetailsModal.baseFoodError ? (
                <div role="alert" style={{ color: 'var(--crm-danger)' }}>{productDetailsModal.baseFoodError}</div>
              ) : productDetailsModal.baseFood ? (
                <div style={{ display: 'grid', gap: '10px' }}>
                  <div className="crm-mobile-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(90px, 1fr))', gap: '8px' }}>
                    {[
                      ['Ккал', productDetailsModal.baseFood.energyKcal.toFixed(2), ''],
                      ['Білки', productDetailsModal.baseFood.protein.toFixed(3), 'г'],
                      ['Жири', productDetailsModal.baseFood.fat.toFixed(3), 'г'],
                      ['Вуглеводи', productDetailsModal.baseFood.carbohydrates.toFixed(3), 'г'],
                      ['Клітковина', productDetailsModal.baseFood.fiber.toFixed(3), 'г'],
                      ['Вода', productDetailsModal.baseFood.water.toFixed(3), 'г'],
                      ['Зола', productDetailsModal.baseFood.ash.toFixed(3), 'г'],
                    ].map(([label, value, unit]) => (
                      <div key={label} style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '8px' }}>
                        <div style={{ color: 'var(--crm-text-muted)', fontSize: 11 }}>{label} / 100 г</div>
                        <div style={{ fontWeight: 700, color: 'var(--crm-text)' }}>{value} {unit}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: 6 }}>Вітаміни, мікро- та макроелементи / 100 г</div>
                    {productDetailsModal.baseFood.nutrients.length === 0 ? (
                      <div style={{ color: 'var(--crm-text-muted)' }}>Додаткові нутрієнти ще не заповнені. Їх можна імпортувати Excel-шаблоном на сторінці «Нутрієнтна база».</div>
                    ) : (
                      <div style={{ maxHeight: 180, overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ background: 'var(--crm-surface-alt)' }}>
                              <th style={{ ...cellStyle, fontWeight: 700 }}>Група</th>
                              <th style={{ ...cellStyle, fontWeight: 700 }}>Нутрієнт</th>
                              <th style={{ ...cellStyle, fontWeight: 700, textAlign: 'right' }}>Кількість</th>
                              <th style={{ ...cellStyle, fontWeight: 700 }}>Од.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productDetailsModal.baseFood.nutrients.map((nutrient) => (
                              <tr key={nutrient.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                                <td style={cellStyle}>{nutrient.group || 'Other'}</td>
                                <td style={{ ...cellStyle, fontWeight: 500 }}>{nutrient.name}</td>
                                <td style={{ ...cellStyle, textAlign: 'right' }}>{nutrient.amountPer100g.toFixed(3)}</td>
                                <td style={cellStyle}>{nutrient.unit || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '10px' }}>
              <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px' }}>Привязка товару</div>
              {productDetailsModal.linkedSupplierProducts.length === 0 ? (
                <div style={{ color: 'var(--crm-text-muted)' }}>Немає активних привʼязок.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Постачальник</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Ціна</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productDetailsModal.linkedSupplierProducts.map((sp) => (
                      <tr key={`sp-${sp.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={cellStyle}>{sp.supplierName}</td>
                        <td style={{ ...cellStyle, fontWeight: 500 }}>{sp.supplierProductName}</td>
                        <td style={cellStyle}>{sp.effectiveUnitPrice.toFixed(2)} ₴</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '10px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <button
                  type="button"
                  onClick={() => setProductDetailsModal((prev) => prev ? { ...prev, activeTab: 'usage' } : prev)}
                  style={{
                    border: '1px solid var(--crm-border)',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: productDetailsModal.activeTab === 'usage' ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)',
                    color: productDetailsModal.activeTab === 'usage' ? 'var(--crm-info)' : 'var(--crm-text)',
                  }}
                >
                  Входження у стравах та Н/Ф
                </button>
                <button
                  type="button"
                  onClick={() => setProductDetailsModal((prev) => prev ? { ...prev, activeTab: 'receipts' } : prev)}
                  style={{
                    border: '1px solid var(--crm-border)',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: productDetailsModal.activeTab === 'receipts' ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)',
                    color: productDetailsModal.activeTab === 'receipts' ? 'var(--crm-info)' : 'var(--crm-text)',
                  }}
                >
                  Приходи
                </button>
                <button
                  type="button"
                  onClick={() => setProductDetailsModal((prev) => prev ? { ...prev, activeTab: 'usage-report' } : prev)}
                  style={{
                    border: '1px solid var(--crm-border)',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: productDetailsModal.activeTab === 'usage-report' ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)',
                    color: productDetailsModal.activeTab === 'usage-report' ? 'var(--crm-info)' : 'var(--crm-text)',
                  }}
                >
                  Звіт по реалізації продукту
                </button>
                <button
                  type="button"
                  onClick={() => printProductDetailsModal(productDetailsModal)}
                  style={{
                    marginLeft: 'auto',
                    border: '1px solid var(--crm-border)',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: 'var(--crm-success-bg)',
                    color: 'var(--crm-success)',
                  }}
                >
                  Друк
                </button>
                <button
                  type="button"
                  onClick={handleRebuildCompletedOrderReport}
                  style={{
                    border: '1px solid var(--crm-border)',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    background: 'var(--crm-blocked-bg)',
                    color: 'var(--crm-blocked)',
                  }}
                >
                  Оновити звіт
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'end', marginBottom: '10px', flexWrap: 'wrap' }}>
                <label style={{ ...labelStyle, minWidth: '180px' }}>
                  З
                  <input type="date" value={receiptRangeFrom} onChange={(e) => setReceiptRangeFrom(e.target.value)} style={inputStyle} />
                </label>
                <label style={{ ...labelStyle, minWidth: '180px' }}>
                  По
                  <input type="date" value={receiptRangeTo} onChange={(e) => setReceiptRangeTo(e.target.value)} style={inputStyle} />
                </label>
                <button type="button" onClick={handleApplyProductDetailsRange} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}>
                  Застосувати
                </button>
              </div>

              {productDetailsModal.activeTab === 'usage' ? (
                productDetailsModal.usageEntries.length === 0 ? (
                  <div style={{ color: 'var(--crm-text-muted)' }}>Немає входжень у стравах або напівфабрикатах.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--crm-surface-alt)' }}>
                        <th style={{ ...cellStyle, fontWeight: 700 }}>Тип</th>
                        <th style={{ ...cellStyle, fontWeight: 700 }}>ID</th>
                        <th style={{ ...cellStyle, fontWeight: 700 }}>Назва</th>
                        <th style={{ ...cellStyle, fontWeight: 700 }}>Кількість продукту</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productDetailsModal.usageEntries.map((entry) => (
                        <tr key={`${entry.kind}-${entry.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                          <td style={cellStyle}>{entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
                          <td style={cellStyle}>{entry.id}</td>
                          <td style={{ ...cellStyle, fontWeight: 500 }}>{entry.name}</td>
                          <td style={cellStyle}>{entry.quantity.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              ) : (
                productDetailsModal.activeTab === 'usage-report' ? (
                  productDetailsModal.receiptsLoading ? (
                    <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження звіту...</div>
                  ) : productDetailsModal.receiptsError ? (
                    <div role="alert" style={{ color: 'var(--crm-danger)' }}>{productDetailsModal.receiptsError}</div>
                  ) : productDetailsModal.usageReportEntries.length === 0 ? (
                    <div style={{ color: 'var(--crm-text-muted)' }}>За вибраний діапазон реалізації не знайдено.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--crm-surface-alt)' }}>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Дата</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Замовлення</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Документ</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Тип</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Кількість</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productDetailsModal.usageReportEntries.map((entry) => (
                          <tr key={`${entry.kind}-${entry.documentId}-${entry.date}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                            <td style={cellStyle}>{new Date(entry.date).toLocaleDateString('uk-UA')}</td>
                            <td style={cellStyle}>{entry.orderLabel}</td>
                            <td style={{ ...cellStyle, fontWeight: 500 }}>{entry.documentName}</td>
                            <td style={cellStyle}>{entry.kind}</td>
                            <td style={cellStyle}>{entry.quantity.toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : productDetailsModal.activeTab === 'receipts' ? (
                  productDetailsModal.receiptsLoading ? (
                    <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження приходів...</div>
                  ) : productDetailsModal.receiptsError ? (
                    <div role="alert" style={{ color: 'var(--crm-danger)' }}>{productDetailsModal.receiptsError}</div>
                  ) : productDetailsModal.receiptEntries.length === 0 ? (
                    <div style={{ color: 'var(--crm-text-muted)' }}>За вибраний діапазон приходів цього товару не знайдено.</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--crm-surface-alt)' }}>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Дата</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Накладна</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Постачальник</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>К-сть</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Ціна</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>ПДВ</th>
                          <th style={{ ...cellStyle, fontWeight: 700 }}>Сума</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productDetailsModal.receiptEntries.map((entry) => (
                          <tr key={`receipt-${entry.receiptId}-${entry.date}-${entry.quantity}-${entry.unitPrice}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                            <td style={cellStyle}>{new Date(entry.date).toLocaleDateString('uk-UA')}</td>
                            <td style={{ ...cellStyle, fontWeight: 500 }}>№{entry.invoiceNumber || entry.receiptId}</td>
                            <td style={cellStyle}>{entry.supplierName || '—'}</td>
                            <td style={cellStyle}>{entry.quantity.toFixed(4)} {WEIGHT_TYPES.find((w) => w.value === entry.weightType)?.label ?? entry.weightType}</td>
                            <td style={cellStyle}>{entry.unitPrice.toFixed(2)} ₴</td>
                            <td style={cellStyle}>
                              {entry.supplierVatRate > 0 ? `${entry.vatAmount.toFixed(2)} ₴ (${entry.supplierVatRate.toFixed(2)}%)` : '—'}
                            </td>
                            <td style={cellStyle}>{entry.lineTotal.toFixed(2)} ₴</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                ) : productDetailsModal.receiptsLoading ? (
                  <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження приходів...</div>
                ) : productDetailsModal.receiptsError ? (
                  <div role="alert" style={{ color: 'var(--crm-danger)' }}>{productDetailsModal.receiptsError}</div>
                ) : productDetailsModal.receiptEntries.length === 0 ? (
                  <div style={{ color: 'var(--crm-text-muted)' }}>За вибраний діапазон приходів цього товару не знайдено.</div>
                ) : (
                  <></>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {deleteBlockModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '760px', maxHeight: '80vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', boxShadow: 'var(--crm-shadow-sm)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-danger)' }}>{deleteBlockModal.title}</h3>
              <button type="button" onClick={() => { setDeleteBlockModal(null); setDeleteReplaceTargetId(''); }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-md)', padding: '10px 12px', fontSize: '14px' }}>
              {deleteBlockModal.message}
            </div>
            {deleteBlockModal.entries.length === 0 ? (
              <div style={{ color: 'var(--crm-text-muted)' }}>Входження у стравах/Н-Ф не знайдено у поточному списку. Перевірте привʼязки в накладних, ревізіях, списаннях або закупівельних листах.</div>
            ) : (
              <>
                <div style={{ color: 'var(--crm-text)', fontWeight: 600 }}>Куди входить:</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Тип</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>ID</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Кількість продукту</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deleteBlockModal.entries.map((entry) => (
                      <tr key={`delete-block-${entry.kind}-${entry.id}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={cellStyle}>{entry.kind === 'dish' ? 'Страва' : 'Н/Ф'}</td>
                        <td style={cellStyle}>{entry.id}</td>
                        <td style={{ ...cellStyle, fontWeight: 500 }}>{entry.name}</td>
                        <td style={cellStyle}>{entry.quantity.toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {deleteBlockModal.references.length > 0 && (
              <>
                <div style={{ color: 'var(--crm-text)', fontWeight: 600 }}>Привʼязки за ID продукту у документах:</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Тип</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>ID документа</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Назва/№</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>Дата</th>
                      <th style={{ ...cellStyle, fontWeight: 700 }}>ID рядка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deleteBlockModal.references.map((ref) => (
                      <tr key={`delete-ref-${ref.scope}-${ref.documentId}-${ref.lineId}`} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={cellStyle}>{getDeleteReferenceTypeLabel(ref.scope)}</td>
                        <td style={cellStyle}>{ref.documentId}</td>
                        <td style={{ ...cellStyle, fontWeight: 500 }}>{ref.documentLabel || '—'}</td>
                        <td style={cellStyle}>{ref.date ? new Date(ref.date).toLocaleDateString('uk-UA') : '—'}</td>
                        <td style={cellStyle}>{ref.lineId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {deleteReplaceSourceProduct && !canReplaceProducts && (
              <div role="note" style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '12px', color: 'var(--crm-text-muted)' }}>
                {replacementUnavailableNotice}
              </div>
            )}
            {deleteReplaceSourceProduct && canReplaceProducts && (
              <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ color: 'var(--crm-text)', fontWeight: 600 }}>
                  Швидка заміна перед видаленням
                </div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>
                  Оберіть товар-заміну для <strong>{deleteReplaceSourceProduct.name}</strong>, після чого система одразу спробує видалити поточний товар.
                </div>
                <SearchSelect
                  items={deleteReplaceProductSearchItems}
                  value={deleteReplaceTargetId}
                  onChange={setDeleteReplaceTargetId}
                  placeholder="Оберіть товар для швидкої заміни..."
                  minWidth={260}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    disabled={replacing}
                    onClick={handleReplaceFromDeleteModal}
                    style={{ background: replacing ? 'var(--crm-surface-alt)' : 'var(--crm-warning-bg)', color: replacing ? 'var(--crm-text-muted)' : 'var(--crm-warning)', border: `1px solid ${replacing ? 'var(--crm-border)' : 'var(--crm-warning)'}`, borderRadius: 'var(--crm-radius-sm)', padding: '10px 16px', cursor: replacing ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                  >
                    {replacing ? 'Заміна...' : 'Замінити і видалити'}
                  </button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setDeleteBlockModal(null); setDeleteReplaceTargetId(''); }} style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '10px 18px', cursor: 'pointer', fontWeight: 600 }}>
                Зрозуміло
              </button>
            </div>
          </div>
        </div>
      )}
      {b52ModalOpen && (
        <B52Modal
          title="Товари"
          entityType="products"
          onClose={() => setB52ModalOpen(false)}
          onSaved={() => dispatch(fetchProducts())}
        />
      )}
    </div>
  );
};

export default ProductsPage;
